import React, { useEffect } from "react";
import { InputNumber } from "primereact/inputnumber";
import { FileUpload } from "primereact/fileupload";
import { Button } from "primereact/button";
import localeText from "../../../../../locale";
import { useBankTransfer } from "../../Checkout/hooks/useBankTransfer";
import { usePayment } from "../../Checkout/context/PaymentContext";

import "../SinglePaymentSelection.css";

const SinglePaymentTransfer = ({
  currencyProduct,
  currencyFormat = {},
  toast,
  combinePayment,
  totalPrice,
}) => {
  const {
    selectedPaymentMethod,
    setAmountBankTransfer,
    isPaymentMethodAvailable,
    isTotalCovered,
    isPaymentMethodInUse,
    setAmountExtracredit,
    setAmountWallet,
  } = usePayment();
  const {
    amountBankTransfer,
    maxAmount,
    bankTransferReceipt,
    loading,
    fileUploadRef,
    handleAmountChange,
    uploadReceipt,
    canProceed,
    clearBankTransfer,
    removeReceipt,
  } = useBankTransfer(toast);

  useEffect(() => {
    if (!combinePayment && selectedPaymentMethod !== "bankTransfer") {
      if (amountBankTransfer !== 0 || bankTransferReceipt !== null) {
        clearBankTransfer();
      }
    }
  }, [
    selectedPaymentMethod,
    clearBankTransfer,
    combinePayment,
    amountBankTransfer,
    bankTransferReceipt,
  ]);

  useEffect(() => {
    if (!combinePayment && selectedPaymentMethod === "bankTransfer") {
      setAmountExtracredit(0);
      setAmountWallet(0);
      setAmountBankTransfer(totalPrice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPaymentMethod, combinePayment, totalPrice]);

  const isAvailable = isPaymentMethodAvailable("bankTransfer");
  const totalCovered = isTotalCovered();
  const isInUse = isPaymentMethodInUse("bankTransfer");

  if (combinePayment && totalCovered && !isInUse) {
    return (
      <div className="flex flex-column gap-2">
        <div className="p-3 bg-blue-50 border-round border-blue-200">
          <div className="flex align-items-center gap-2">
            <i className="pi pi-info-circle text-blue-600"></i>
            <span className="text-blue-800 font-medium">{localeText("totalAlreadyCovered")}</span>
          </div>
          <small className="text-blue-700 mt-1 block">
            {localeText("paymentCompleteWithOtherMethodsExplanation")}
          </small>
        </div>
      </div>
    );
  }

  if (combinePayment && !isAvailable) {
    return (
      <div className="flex flex-column gap-2">
        <div className="p-3 bg-gray-50 border-round border-gray-200">
          <div className="flex align-items-center gap-2">
            <i className="pi pi-ban text-gray-600"></i>
            <span className="text-gray-800 font-medium">{localeText("methodNotAvailable")}</span>
          </div>
          <small className="text-gray-700 mt-1 block">
            {localeText("methodNotAvailableExplanation")}
          </small>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-column justify-content-between gap-2 w-full">
      {combinePayment && (
        <div className="flex align-items-center xl:flex-row gap-2 w-full">
          <span className="text-600">{currencyFormat[currencyProduct]}</span>
          <InputNumber
            className="input-amount"
            style={{ width: "100%" }}
            value={amountBankTransfer}
            onChange={(e) => handleAmountChange(e.value, combinePayment, totalPrice)}
            max={maxAmount}
            minFractionDigits={2}
            maxFractionDigits={2}
            disabled={loading || !combinePayment || !isAvailable}
          />
        </div>
      )}

      {(amountBankTransfer > 0 || !combinePayment) && (
        <div className="mt-3">
          <div>
            <div className="text-md" style={{ color: "#505F79" }}>
              {localeText("transferToThisBank")}
              <ul
                style={{
                  lineHeight: "1.5",
                  marginTop: "0.5rem",
                  marginBottom: "0",
                  paddingLeft: "1.5rem",
                }}
              >
                <li>
                  {localeText("value")}:{" "}
                  <b>
                    {currencyFormat[currencyProduct]}{" "}
                    {combinePayment
                      ? amountBankTransfer
                      : parseFloat((Number(totalPrice) || 0).toFixed(2))}
                  </b>
                </li>
                <li>
                  {localeText("bank")}: <b>BPM</b>
                </li>
                <li style={{ wordBreak: "break-all" }}>
                  <b>IBAN: IT78F0503403268000000024931</b>
                </li>
              </ul>
            </div>
          </div>

          <div
            className="responsive-info-text flex align-items-center gap-2 py-4"
            style={{ color: "#245AE5" }}
          >
            <i className="pi pi-info-circle" style={{ fontSize: "1.2rem" }} />
            {localeText("afterTransferUploadReceipt")}
          </div>

          <div className="mt-2">
            {!bankTransferReceipt ? (
              <FileUpload
                name="bankTransferReceipt"
                mode="basic"
                auto
                customUpload
                uploadHandler={uploadReceipt}
                ref={fileUploadRef}
                accept="image/*"
                maxFileSize={5000000} // 5MB
                chooseOptions={{
                  icon: "none",
                  label: (
                    <div
                      className="font-semibold"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "1rem",
                      }}
                    >
                      <i className="pi pi-cloud-upload" style={{ fontSize: "1.4rem" }} />
                      {localeText("uploadReceipt")}
                    </div>
                  ),
                  style: {
                    backgroundColor: "#ECF1FF",
                    color: "#245AE5",
                    borderRadius: "6px",
                    padding: "2rem 0",
                    cursor: "pointer",
                    border: "dashed 1px #245AE5",
                    outline: "none",
                    boxShadow: "none",
                    width: "100%",
                  },
                }}
                className="custom-file-upload"
              />
            ) : (
              <div
                className="flex align-items-center justify-content-between p-3 border-1 border-round"
                style={{ borderColor: "#e9ecef" }}
              >
                <div className="flex align-items-center gap-2">
                  <i className="pi pi-check-circle text-green-500" style={{ fontSize: "1.2rem" }} />
                  <span className="text-green-700 font-semibold">
                    {localeText("receiptUploadedSuccessfully")}
                  </span>
                </div>
                <Button
                  icon="pi pi-times"
                  className="p-button-rounded p-button-text p-button-sm"
                  onClick={removeReceipt}
                  tooltip={localeText("removeReceipt")}
                  tooltipOptions={{ position: "top" }}
                  style={{ color: "#dc3545" }}
                />
              </div>
            )}

            {(amountBankTransfer > 0 || !combinePayment) && !canProceed() && (
              <div className="mt-3 text-xs" style={{ color: "#505F79" }}>
                {localeText("bankTransferImportantNote")}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SinglePaymentTransfer;
